import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProtectedLayout from "@/layouts/ProtectedLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { OrderCard } from "@/components/orders/OrderCard";
import { toast } from "sonner";
import { Plus, Package, Clock, CheckCircle, XCircle } from "lucide-react";

interface Order {
  id: string;
  publicId: string;
  orderType: string;
  status: string;
  createdAt: string;
  promisedDeliveryAt: string | null;
  area: { name: string };
  address: { line1: string; city: string };
  items: { garmentType: string; qty: number }[];
  pickupSlot: { startAt: string; endAt: string } | null;
}

const ACTIVE_STATUSES = [
  "CREATED",
  "PICKUP_SCHEDULED",
  "PICKUP_ASSIGNED",
  "PICKUP_IN_PROGRESS",
  "PICKED_UP",
  "RECEIVED_AT_WORKSHOP",
  "INSPECTION_IN_PROGRESS",
  "QUOTE_SENT",
  "AWAITING_PAYMENT",
  "PAID",
  "IN_PRODUCTION",
  "QC",
  "READY_FOR_DELIVERY",
  "DELIVERY_ASSIGNED",
  "OUT_FOR_DELIVERY",
];

const COMPLETED_STATUSES = ["DELIVERED", "CLOSED"];
const CANCELLED_STATUSES = ["CANCELLED", "RETURNED"];

export default function CustomerOrdersPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<Order[]>([]);

  useEffect(() => {
    fetchOrders();
  }, []);

  async function fetchOrders() {
    try {
      const response = await fetch("/api/customer/orders?limit=50", { credentials: "same-origin" });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      } else {
        toast.error("Failed to load orders");
      }
    } catch (error) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }

  const activeOrders = orders.filter((o) => ACTIVE_STATUSES.includes(o.status));
  const completedOrders = orders.filter((o) => COMPLETED_STATUSES.includes(o.status));
  const cancelledOrders = orders.filter((o) => CANCELLED_STATUSES.includes(o.status));

  const content = loading ? (
    <div className="container mx-auto px-4 md:px-6 py-6 space-y-6">
      <PageHeader title="My Orders" />
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    </div>
  ) : (
    <div className="container mx-auto px-4 md:px-6 py-6 space-y-6">
      <PageHeader
        title="My Orders"
        actions={
          <Button onClick={() => navigate("/customer/orders/new")}>
            <Plus className="h-4 w-4 mr-2" />
            New Order
          </Button>
        }
      />

      <Tabs defaultValue="active" className="w-full">
        <TabsList>
          <TabsTrigger value="active" className="gap-2">
            <Clock className="h-4 w-4" />
            Active
            {activeOrders.length > 0 && (
              <span className="ml-1 text-xs bg-primary/20 px-1.5 rounded-full">
                {activeOrders.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Completed
          </TabsTrigger>
          <TabsTrigger value="cancelled" className="gap-2">
            <XCircle className="h-4 w-4" />
            Cancelled
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6">
          {activeOrders.length > 0 ? (
            <div className="space-y-4">
              {activeOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onView={() => navigate(`/customer/orders/${order.id}`)}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <EmptyState
                  icon={Package}
                  title="No Active Orders"
                  description="You don't have any orders in progress. Start a new order to get your garments altered or stitched."
                  action={
                    <Button onClick={() => navigate("/customer/orders/new")}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create New Order
                    </Button>
                  }
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="completed" className="mt-6">
          {completedOrders.length > 0 ? (
            <div className="space-y-4">
              {completedOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onView={() => navigate(`/customer/orders/${order.id}`)}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <EmptyState
                  icon={CheckCircle}
                  title="No Completed Orders"
                  description="Your completed orders will appear here."
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="cancelled" className="mt-6">
          {cancelledOrders.length > 0 ? (
            <div className="space-y-4">
              {cancelledOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onView={() => navigate(`/customer/orders/${order.id}`)}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <EmptyState
                  icon={XCircle}
                  title="No Cancelled Orders"
                  description="Your cancelled orders will appear here."
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );

  return <ProtectedLayout>{content}</ProtectedLayout>;
}